import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Check, Search } from 'lucide-react';

const CompanyDropdown = ({
  companies,
  selectedCompany,
  onSelectCompany,
  isLoading,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get selected company object
  const selectedCompanyObj = companies.find(c => c.companyId === selectedCompany);

  // Filter companies based on search
  const filteredCompanies = companies.filter(company =>
    company.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.location && company.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSelect = (company) => {
    onSelectCompany(company);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Dropdown Button */}
      <button
        onClick={toggleDropdown}
        disabled={disabled || isLoading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.625rem 1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '10px',
          cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: '500',
          transition: 'all 0.2s ease',
          minWidth: '200px',
          justifyContent: 'space-between',
          opacity: disabled || isLoading ? 0.6 : 1
        }}
        onMouseOver={(e) => {
          if (!disabled && !isLoading) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
          }
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Building2 style={{ width: '1rem', height: '1rem' }} />
          <span style={{
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {isLoading
              ? 'Loading...'
              : selectedCompanyObj
                ? selectedCompanyObj.companyName
                : 'Select Company'
            }
          </span>
        </div>
        <ChevronDown
          style={{
            width: '1rem',
            height: '1rem',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: '280px',
            maxWidth: '350px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'dropdownFade 0.2s ease'
          }}
        >
          {/* Search Input */}
          {companies.length > 5 && (
            <div style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <Search style={{ width: '1rem', height: '1rem', color: '#94a3b8' }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search companies..."
                  style={{
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '0.875rem',
                    width: '100%',
                    color: '#1e293b'
                  }}
                />
              </div>
            </div>
          )}

          {/* Company List */}
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '0.5rem'
          }}>
            {filteredCompanies.length === 0 ? (
              <div style={{
                padding: '1rem',
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: '0.875rem'
              }}>
                No companies found
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <button
                  key={company.companyId}
                  onClick={() => handleSelect(company)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    backgroundColor: selectedCompany === company.companyId
                      ? '#eff6ff'
                      : 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => {
                    if (selectedCompany !== company.companyId) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedCompany !== company.companyId) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: company.companyId === 'all' ? '#4F7CFF' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: company.companyId === 'all' ? 'white' : '#64748b',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {company.companyId === 'all'
                        ? 'ALL'
                        : company.companyName.charAt(0).toUpperCase()
                      }
                    </div>
                    <div>
                      <div style={{
                        fontWeight: selectedCompany === company.companyId ? '600' : '500',
                        color: selectedCompany === company.companyId ? '#4F7CFF' : '#1e293b',
                        fontSize: '0.875rem'
                      }}>
                        {company.companyName}
                      </div>
                      {company.location && company.location !== 'All Locations' && (
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#94a3b8',
                          marginTop: '2px'
                        }}>
                          {company.location}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedCompany === company.companyId && (
                    <Check style={{
                      width: '1rem',
                      height: '1rem',
                      color: '#4F7CFF'
                    }} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes dropdownFade {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CompanyDropdown;
